// The landing page's (/) fixed copy and demo data.
//
// Everything on the page that an administrator can change — plan names,
// prices, inclusions, add-on prices, the payment QR and account name — is
// read from the database instead (Subscription Plans and Admin Settings).
// What is left here is marketing copy and the illustrative data behind the
// interactive till in the hero, which is not anybody's real stock.

/** The product's name as the landing page brands it: "Kawnter" + "POS". */
export const LANDING_BRAND_NAME = 'Kawnter';
export const LANDING_BRAND_SUFFIX = 'POS';
export const LANDING_LOGO_URL = `${process.env.PUBLIC_URL || ''}/kawnter_logo.png`;

/** Shown in the browser tab while the landing page is open. */
export const LANDING_DOCUMENT_TITLE = 'Kawnter POS — Ring it up. Know where it went.';

/** The headline, repeated on the pricing card under the plan name. */
export const LANDING_HEADLINE = ['Say goodbye to stock chaos.', 'Smarter, hassle-free sales start here.'];

/** Section anchors, used by the nav, the drawer and the footer. */
export const LANDING_SECTIONS = {
  work: 'work',
  pricing: 'pricing',
  faq: 'faq',
  till: 'till',
  custom: 'custom',
};

export const LANDING_CONTACT_EMAIL = 'hello@thewebsitepal.com';

/**
 * Where "Privacy Policy" in the checkout consent line links to. Blank until
 * the policy page exists — the words then show without a link rather than as
 * a link that goes nowhere.
 */
export const LANDING_PRIVACY_URL = '';
export const LANDING_COPYRIGHT = '© 2026 Kawnter POS — by The Website Pal · Cebu, Philippines';

/** The hero's interactive till. Illustrative only — nothing is saved. */
export const LANDING_DEMO_CATALOGUE = [
  { name: 'Pocari sweat', price: 120, stock: 48 },
  { name: 'Gatorade', price: 65, stock: 31 },
  { name: 'Iced matcha', price: 180, stock: 6 },
  { name: 'Breakfast meal', price: 350, stock: 12 },
];

export const LANDING_DEMO_PAY_MODES = [
  { key: 'Cash', symbol: '💵' },
  { key: 'GCash', symbol: '📱' },
  { key: 'Maya', symbol: '💳' },
  { key: 'BDO', symbol: '🏦' },
];

/** How long "Paid with … — receipt saved" stays before the demo order clears. */
export const LANDING_DEMO_RESET_MS = 1400;

/** The proof strip under the hero. */
export const LANDING_TESTIMONIAL = {
  quote: 'We stopped guessing what the fridge made each day. The numbers just come out at closing.',
  cite: 'Court District Cebu — running Kawnter since March 2026',
};

export const LANDING_STATS = [
  { to: 42800, suffix: '', label: 'Orders rung up' },
  { to: 7, suffix: '', label: 'Businesses live' },
  { to: 100, suffix: '%', label: 'Sales with an audit trail' },
];

/** "See it work" — one tab per screen, each paired with an illustration. */
export const LANDING_SHOWCASE = [
  {
    key: 'counter',
    tab: 'The counter',
    title: 'Built for one hand and a busy queue',
    body: [
      'Big tap targets, category filters and a search that finds an item in two letters. Works on the tablet already sitting on your counter — no special hardware, no install.',
      'Cash, card or e-wallet gets recorded against the order, and the receipt prints or saves as a PDF.',
    ],
    floatSeconds: 11,
    floatDelay: 0,
  },
  {
    key: 'stock',
    tab: 'Stock',
    title: 'The shelf count you can actually trust',
    body: [
      'Stock moves in the same transaction as the sale, not in a background job that might fail quietly. Set a low-stock threshold per item and the counter flags it before you run out mid-rush.',
      'Deliveries and wastage get logged the same way, so the count always explains itself.',
    ],
    floatSeconds: 13,
    floatDelay: -3,
  },
  {
    key: 'sales',
    tab: 'Sales',
    title: 'Closing time takes a minute, not an evening',
    body: [
      'Pick any date range and see takings, order count and your best sellers ranked. Compare this week to last without exporting anything.',
      'When your accountant wants it, one button sends the whole range to Excel.',
    ],
    floatSeconds: 12,
    floatDelay: -6,
  },
  {
    key: 'transactions',
    tab: 'Transactions',
    title: 'Every change has a name on it',
    body: [
      'Price edits, voided orders, stock corrections and new products each write an entry naming the staff member and the exact time. The log is append-only — nobody can tidy it up afterwards.',
      'Give each staff member their own login and the trail becomes a straight answer to "who changed this?"',
    ],
    floatSeconds: 14,
    floatDelay: -2,
  },
];

/** "Built around your counter". */
export const LANDING_CHIPS = [
  'Cafés', 'Retail counters', 'Pickleball courts', 'Gyms and studios', 'Canteens', 'Pro shops', 'Event booths',
];

export const LANDING_CHECKS = [
  { title: 'Your products and categories', body: 'Set up the menu the way your staff already think about it.' },
  { title: 'Your receipt and your tax setup', body: 'Business name, address, VAT lines and footer — printed or saved as PDF.' },
  { title: 'Your staff and what they can touch', body: 'Separate logins with permissions, so cashiers sell and owners see the money.' },
  { title: 'Your own address', body: 'Each business runs at its own till URL. Nothing is shared between them.' },
];

/** The payment apps offered at checkout, and how to pay with each. */
export const LANDING_PAY_APPS = [
  { key: 'GCash', label: 'GCash', how: 'Open GCash, tap Send Money, then Scan QR.' },
  { key: 'Maya', label: 'Maya', how: 'Open Maya, tap Scan, then point at the QR.' },
  { key: 'Online banking', label: 'Online banking / BDO', how: 'Open your bank app, choose InstaPay, then Scan QR.' },
];

/** The product cap quoted in the FAQ. */
export const LANDING_PRODUCT_CAP = 300;

/** How long an expired account's data is kept, as the FAQ quotes it. */
export const LANDING_DATA_RETENTION = '6 months';
